/** Flow modules: `flow.escalation` (difficulty curves → named scalars) and
 * `flow.objective` (win/lose rules + the proof milestone). */
import { bool, id, int, list, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, type ModuleCtx } from '../module.js';
import type { EntityV1, WorldV1 } from '../world.js';

export type CurveDef = {
  /** Scalar written (read by behaviours, e.g. `enemy.speed`). */
  scalar: string;
  /** Index scalar the curve is a function of (`phase.number`, `phase.loop`). */
  index: string;
  kind: 'geometric' | 'linear';
  base: number; rate: number; min: number; max: number;
};
export type EscalationConfig = { curves: CurveDef[] };

/** The one curve evaluator: geometric base·rate^n or linear base + rate·n,
 * clamped. `n` is the index scalar (0-based for loop, 1-based phase → n-1). */
export function evaluateCurve(curve: CurveDef, n: number): number {
  const raw = curve.kind === 'geometric' ? curve.base * curve.rate ** n : curve.base + curve.rate * n;
  return Math.min(curve.max, Math.max(curve.min, raw));
}

export const escalation = defineModule<EscalationConfig>({
  id: 'flow.escalation', version: 1, attach: 'global', role: 'flow',
  summary: 'Difficulty curves: named scalars as geometric/linear functions of a progression index, clamped.',
  config: obj({ curves: list(obj({ scalar: id(), index: id(), kind: oneOf('geometric', 'linear'), base: num(-1000, 1000),
    rate: num(-100, 100), min: num(-1000, 1000), max: num(-1000, 1000) }), 12, 1) }),
  requires: [], events: [], presentation: {}, proofs: ['geometric-curve', 'linear-curve', 'clamped', 'follows-index-scalar'],
  animStates: () => [],
  check: c => c.curves.filter(k => k.min > k.max).map(k => `curve ${k.scalar}: min > max`),
  initGlobal(ctx) { for (const k of ctx.config.curves) ctx.setScalar(k.scalar, evaluateCurve(k, indexOf(ctx.scalar(k.index, 0), k.index))); },
  systems: {
    // Runs before behaviours act on the next tick (flow is after move).
    flow(ctx) { for (const k of ctx.config.curves) ctx.setScalar(k.scalar, evaluateCurve(k, indexOf(ctx.scalar(k.index, 0), k.index))); },
  },
});

/** 1-based counters (`*.number`) start their curve at n = 0. */
function indexOf(value: number, name: string): number {
  return name.endsWith('.number') ? Math.max(0, value - 1) : value;
}

export type ObjectiveConfig = {
  /** `goal`: a checklist module (rule.targets) decides — won once it sets
   * the `goal.done` scalar, progress is its `goal.progress` in [0, 1]. */
  win: 'phases-cleared' | 'clear-required' | 'collect-all' | 'survive' | 'goal' | 'none' | 'last-standing' | 'first-to' | 'most-at-time';
  /** Phases to clear / ticks to survive / pickups the player must really
   * collect (collect-all) / a versus target score (first-to) or match length
   * in ticks (most-at-time); unused by clear/none/last-standing. */
  count: number;
  /** For endless (`none`) games: phases a proof run must clear. */
  milestone: number;
  lose: 'players-out';
  requiredTag: string;
  collectTag: string;
  celebrate: boolean;
  /** Absent/draw retains the original timed-match policy. */
  tieBreak?: 'draw' | 'sudden-death';
  /** Cumulative elapsed active-play ticks, never reset. Missing holds at zero;
   * absent uses world.tick. Its producer may pause it during kickoff, etc. */
  elapsedTicksScalar?: string;
  /** Optional tied sudden-death limit in the selected clock's ticks. Absent
   * keeps overtime unbounded. A lead on the final tick wins before the draw. */
  overtimeTicks?: number;
};

export const objective = defineModule<ObjectiveConfig>({
  id: 'flow.objective', version: 1, attach: 'global', role: 'flow',
  summary: 'Win/lose rules (phases cleared, clear required, collect all (count = pickups the player must really collect through rule.pickup; a missed one that scrolled away does not count), survive, a checklist goal, or endless with a proof milestone; lose when every player is out) — or versus last standing, first to a score, or most points at a time. most-at-time optionally reads cumulative elapsedTicksScalar instead of world ticks; tieBreak sudden-death waits for a lead, optionally drawing after overtimeTicks of the same clock.',
  config: obj({ win: oneOf('phases-cleared', 'clear-required', 'collect-all', 'survive', 'goal', 'none', 'last-standing', 'first-to', 'most-at-time'), count: int(0, 100000),
    milestone: int(0, 100), lose: oneOf('players-out'), requiredTag: id(), collectTag: id(), celebrate: bool(), tieBreak: oneOf('draw', 'sudden-death'),
    elapsedTicksScalar: id(), overtimeTicks: int(1, 100000) }, ['tieBreak', 'elapsedTicksScalar', 'overtimeTicks']),
  requires: ['core.vitals'], events: ['won', 'lost'], presentation: { won: { cue: 'win' }, lost: { cue: 'lose' } },
  proofs: ['win-on-phases', 'win-on-clear', 'win-on-collect', 'lose-when-pickups-run-short', 'win-on-survive', 'win-on-goal', 'lose-when-all-out', 'endless-never-wins',
    'versus-last-standing', 'versus-first-to', 'versus-most-at-time', 'versus-draw'],
  animStates: () => [],
  animStatesForTag: c => (c.celebrate ? [{ tag: 'player', states: ['win'] }] : []),
  check(c, context) {
    const errors: string[] = [];
    if (c.win === 'phases-cleared' && !context.globals.includes('flow.director')) errors.push('phases-cleared needs flow.director');
    if (c.win === 'phases-cleared' && c.count < 1) errors.push('phases-cleared needs count ≥ 1');
    if (c.win === 'survive' && c.count < 30) errors.push('survive needs count ≥ 30 ticks');
    if (c.win === 'none' && c.milestone < 1) errors.push('an endless objective needs a proof milestone ≥ 1');
    if (c.win === 'none' && !context.globals.includes('flow.director')) errors.push('an endless objective needs flow.director');
    if (c.win === 'first-to' && c.count < 1) errors.push('first-to needs a target score count ≥ 1');
    if (c.win === 'most-at-time' && c.count < 30) errors.push('most-at-time needs count ≥ 30 ticks');
    if (c.tieBreak === 'sudden-death' && c.win !== 'most-at-time') errors.push('sudden-death tieBreak needs most-at-time');
    if (c.elapsedTicksScalar !== undefined && c.win !== 'most-at-time') errors.push('elapsedTicksScalar needs most-at-time');
    if (c.overtimeTicks !== undefined && (c.win !== 'most-at-time' || c.tieBreak !== 'sudden-death')) errors.push('overtimeTicks needs most-at-time with sudden-death');
    return errors;
  },
  systems: {
    flow(ctx) {
      // Another rule (a time limit, a missed grade) may have decided the
      // match earlier this tick: the objective never announces after it.
      if (ctx.world.phase !== 'active') return;
      const c = ctx.config;
      const players = ctx.entities({ tag: 'player', status: 'any' });
      if (VERSUS_WINS.has(c.win)) { decideVersus(ctx, c, players); return; }
      if (players.length && players.every(p => p.tags.includes('out'))) {
        ctx.emit('lost', { reason: 'players-out' });
        ctx.end('lost', 'players-out');
        return;
      }
      if (c.win === 'collect-all') tallyCollected(ctx);
      let won = false;
      if (c.win === 'phases-cleared') won = ctx.scalar('phase.cleared', 0) >= c.count;
      else if (c.win === 'clear-required') won = ctx.world.tick > 1 && ctx.entities({ tag: c.requiredTag, status: 'any' }).every(e => e.tags.includes('defeated'));
      else if (c.win === 'collect-all') won = ctx.world.tick > 1 && ctx.scalar(COLLECTED_SCALAR, 0) >= c.count && ctx.entities({ tag: c.collectTag, status: 'any' }).length === 0;
      else if (c.win === 'survive') won = ctx.world.tick >= c.count;
      else if (c.win === 'goal') won = ctx.scalar('goal.done', 0) >= 1;
      if (!won && c.win === 'collect-all' && pickupsRunShort(ctx)) {
        ctx.emit('lost', { reason: 'pickups-missed' });
        ctx.end('lost', 'pickups-missed');
        return;
      }
      if (!won) return;
      ctx.emit('won', { reason: c.win });
      ctx.end('won', c.win);
      if (c.celebrate) for (const p of players) if (p.status !== 'hidden') ctx.setAnim(p, 'win');
    },
  },
});

/** Pickups really collected. An emptied field alone is no win: a pickup a
 * scrolling world carried away, or one it has not placed yet, also leaves the
 * field empty (Ember Lift Film Three's idle balloon won at tick 88 this way). */
const COLLECTED_SCALAR = 'objective.collected';

function tallyCollected(ctx: ModuleCtx<ObjectiveConfig>): void {
  const got = ctx.signals('collected').filter(s => (s.data.tags as readonly string[] | undefined)?.includes(ctx.config.collectTag)).length;
  if (got) ctx.setScalar(COLLECTED_SCALAR, ctx.scalar(COLLECTED_SCALAR, 0) + got);
}

/** A collect-all run that can no longer be won: the pickups collected, on
 * the field and still to be placed fall short of `count` (one scrolled away
 * or expired). Never when the game cannot say what is still to come. */
function pickupsRunShort(ctx: ModuleCtx<ObjectiveConfig>): boolean {
  const ahead = ctx.placementsAhead(ctx.config.collectTag);
  return ahead !== null
    && ctx.scalar(COLLECTED_SCALAR, 0) + ctx.entities({ tag: ctx.config.collectTag, status: 'any' }).length + ahead < ctx.config.count;
}

const VERSUS_WINS: ReadonlySet<string> = new Set(['last-standing', 'first-to', 'most-at-time']);

/** The seat with the strictly highest score, or null on a tie. */
function leader(scores: readonly number[]): number | null {
  let best = -Infinity, seat: number | null = null;
  scores.forEach((s, i) => { if (s > best) { best = s; seat = i; } else if (s === best) seat = null; });
  return seat;
}

function elapsedTicks(c: ObjectiveConfig, world: Pick<WorldV1, 'scalars' | 'tick'>): number {
  const ticks = c.elapsedTicksScalar === undefined ? world.tick : world.scalars[c.elapsedTicksScalar] ?? 0;
  return Number.isFinite(ticks) ? Math.max(0, ticks) : 0;
}

/** A versus match's rule: the last seat not out, the first to `count`
 * points (ties at once draw), or most points at `count` on the selected clock,
 * with the configured tie/overtime policy. Every seat out at once draws. */
function decideVersus(ctx: ModuleCtx<ObjectiveConfig>, c: ObjectiveConfig, players: readonly EntityV1[]): void {
  const seats = ctx.world.playerCount;
  const alive = players.filter(p => p.slot !== null && !p.tags.includes('out'));
  let done = false, winner: number | null = null;
  if (seats > 1 && alive.length === 0 && players.length) done = true;
  else if (c.win === 'last-standing') { if (seats > 1 && alive.length === 1) { done = true; winner = alive[0]!.slot; } }
  else if (c.win === 'first-to') {
    const reached = ctx.world.scores.map((s, i) => (s >= c.count ? i : -1)).filter(i => i >= 0);
    if (reached.length) { done = true; winner = reached.length === 1 ? reached[0]! : leader(ctx.world.scores); }
  } else if (c.win === 'most-at-time') {
    const elapsed = elapsedTicks(c, ctx.world);
    if (elapsed >= c.count) {
      winner = leader(ctx.world.scores);
      done = winner !== null || c.tieBreak !== 'sudden-death'
        || (c.overtimeTicks !== undefined && elapsed >= c.count + c.overtimeTicks);
    }
  }
  if (!done) return;
  ctx.emit('won', { reason: winner === null ? 'draw' : c.win, slot: winner ?? -1 });
  ctx.end('won', winner === null ? 'draw' : c.win, winner);
  if (c.celebrate && winner !== null) for (const p of players) if (p.slot === winner && p.status !== 'hidden') ctx.setAnim(p, 'win');
}

/** Progress toward the objective in [0, 1] plus completion, for solvers and
 * the proof harness, from `slot`'s side (versus objectives: a seat's own
 * score or survival; completion is that seat winning). Endless objectives
 * complete at their milestone. */
export function objectiveProgress(config: ObjectiveConfig, world: Pick<WorldV1, 'scalars' | 'phase' | 'tick' | 'scores' | 'winner'>,
  slot: number): { complete: boolean; progress: number } {
  if (VERSUS_WINS.has(config.win)) {
    if (world.phase !== 'active') return { complete: world.winner === slot, progress: world.winner === slot ? 1 : 0 };
    const own = world.scores[slot] ?? 0;
    if (config.win === 'first-to') return { complete: false, progress: Math.min(1, own / config.count) };
    if (config.win === 'most-at-time') return { complete: false, progress: Math.min(1, elapsedTicks(config, world) / config.count) * (leader(world.scores) === slot ? 1 : 0.5) };
    return { complete: false, progress: 0 };
  }
  const { scalars, phase, tick } = world;
  if (phase === 'won') return { complete: true, progress: 1 };
  const cleared = scalars['phase.cleared'] ?? 0;
  switch (config.win) {
    case 'phases-cleared': return { complete: false, progress: cleared / config.count };
    case 'none': return { complete: cleared >= config.milestone, progress: Math.min(1, cleared / config.milestone) };
    case 'survive': return { complete: false, progress: tick / config.count };
    case 'goal': return { complete: false, progress: scalars['goal.progress'] ?? 0 };
    case 'collect-all': return { complete: false, progress: config.count > 0 ? Math.min(1, (scalars[COLLECTED_SCALAR] ?? 0) / config.count) : 0 };
    default: return { complete: false, progress: 0 };
  }
}
